import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import ProjectCard from "@/components/ProjectCard";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { Avatar } from "@/components/UserMenu";
import { fetchFavourites, fetchMyEnquiries } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/meta";

const STATUS_TONE: Record<string, string> = {
  New: "text-blue-300 border-blue-300/40",
  Contacted: "text-gold border-gold/40",
  "In Consultation": "text-emerald-300 border-emerald-300/40",
  Closed: "text-zinc-400 border-zinc-500/40",
};

export default function Account() {
  usePageMeta("My Account | Shriya Redkar Interiors", "Your saved projects and enquiry status with Shriya Redkar Interiors.");
  const { user, loading } = useAuth();
  const favourites = useQuery({ queryKey: ["favourites"], queryFn: fetchFavourites, enabled: !!user });
  const enquiries = useQuery({ queryKey: ["my-enquiries"], queryFn: fetchMyEnquiries, enabled: !!user });

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian">
        <p className="font-mono text-sm text-zinc-500">Checking your session…</p>
      </main>
    );
  }

  if (!user) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-obsidian px-6 pt-[76px]" data-testid="account-signed-out">
        <div className="w-full max-w-md border border-white/10 bg-obsidian-card p-10 text-center">
          <p className="eyebrow">Your account</p>
          <h1 className="mt-4 font-serif text-4xl font-light text-zinc-50">Save the rooms you love.</h1>
          <p className="mt-4 text-sm leading-relaxed text-zinc-400">
            Sign in with Google to bookmark projects and follow the status of your enquiry with the studio.
          </p>
          <GoogleSignInButton returnTo="/account" className="mt-8 w-full" testId="account-google-signin" />
        </div>
      </main>
    );
  }

  return (
    <main className="bg-obsidian pt-[76px]" data-testid="account-page">
      <section className="container-x flex flex-wrap items-center gap-6 py-16 lg:py-20">
        <Avatar user={user} className="h-20 w-20" />
        <div>
          <p className="eyebrow">Welcome back</p>
          <h1 className="mt-2 font-serif text-4xl font-light text-zinc-50 sm:text-5xl" data-testid="account-name">
            {user.name}
          </h1>
          <p className="mt-1 text-sm text-zinc-500" data-testid="account-email">{user.email}</p>
        </div>
      </section>

      <section className="container-x border-t border-white/10 py-16">
        <Reveal>
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Saved projects</p>
              <h2 className="mt-3 font-serif text-3xl text-zinc-50 sm:text-4xl">Rooms you've bookmarked</h2>
            </div>
            <Link to="/portfolio" data-testid="account-browse-portfolio" className="hidden items-center gap-2 font-display text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-400 hover:text-gold sm:inline-flex">
              Browse portfolio <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        {favourites.data?.length ? (
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="account-favourites-grid">
            {favourites.data.map((p) => (
              <div key={p.slug} className="aspect-[4/3]">
                <ProjectCard project={p} />
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 border border-dashed border-white/15 p-10 text-center" data-testid="account-favourites-empty">
            <p className="text-sm text-zinc-400">Nothing saved yet. Open any project and tap “Save project”.</p>
            <Link to="/portfolio" className="btn-ghost mt-6">Explore projects</Link>
          </div>
        )}
      </section>

      <section className="container-x border-t border-white/10 py-16">
        <Reveal>
          <p className="eyebrow">Your enquiries</p>
          <h2 className="mt-3 font-serif text-3xl text-zinc-50 sm:text-4xl">Where things stand</h2>
        </Reveal>
        {enquiries.data?.length ? (
          <ul className="mt-10 divide-y divide-white/10 border-y border-white/10" data-testid="account-enquiries-list">
            {enquiries.data.map((l) => (
              <li key={l._id} data-testid={`account-enquiry-${l._id}`} className="flex flex-wrap items-center justify-between gap-4 py-5">
                <div>
                  <p className="font-serif text-xl text-zinc-100">
                    {l.property_type} · {l.city}
                  </p>
                  <p className="mt-1 text-xs text-zinc-500">
                    {l.budget_band} · {l.timeline} · sent {new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </p>
                </div>
                <span className={`border px-3 py-1.5 font-display text-[10px] uppercase tracking-[0.18em] ${STATUS_TONE[l.status] ?? STATUS_TONE.Closed}`}>
                  {l.status}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-10 border border-dashed border-white/15 p-10 text-center" data-testid="account-enquiries-empty">
            <p className="text-sm text-zinc-400">No enquiries yet. Tell us about your home and we'll call you back within a working day.</p>
            <Link to="/enquire" className="btn-gold mt-6" data-testid="account-cta-enquire">Start an enquiry</Link>
          </div>
        )}
      </section>
    </main>
  );
}
